import {
  clearEmailThreads,
  setEmailThread,
  setEmailThreads,
  setEmailThreadsHasMore,
  setEmailThreadsInitialized,
  setEmailThreadsLoading,
} from '../../store/emailHub/emailThreads'
import { setEmailMetadata, setEmailMetadataLoading } from '../../store/emailHub/emailMetadata'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { selectCurrentUserEmailIntegration, selectEmailContacts } from '../../store/emailHub/emailUsers'
import { EmailFilterPayload } from '../../models/EmailFilter'
import { useFilterEmailThreadsMutation, useLazyGetEmailThreadAttachmentsQuery, useLazyGetEmailThreadQuery } from '../../store/integrations/integrations'
import { useLazyQueryEmailMetadataQuery } from '../../store/integrations/emailMetadata'
import { useCallback } from 'react'
import { useLazyGetEffectiveNodeQuery, useLazyGetInputParametersHierarchyDefaultQuery } from '../../store/ai/inputParametersHierarchy'
import { InputParameterHierarchyType } from '../../components/InputParameterHierarchy/model'
import { EmailType } from '../../models/EmailType'
import { selectEmailTypes } from '../../store/emailHub/emailTypes'
import { EmailMetadata } from '../../models/EmailMetadata'
import { useLazyGetAllTasksQuery } from '../../store/meetingSupport/meetingSupport'
import { setTasks, setTasksLoading } from '../../store/task/task'
import { setEmailThreadAttachments, setEmailThreadAttachmentsLoading } from '../../store/emailHub/emailAttachments'

export const useFetchEmailThreads = () => {
  const accountId = useAppSelector(selectAccountId)

  const dispatch = useAppDispatch()

  const contacts = useAppSelector(selectEmailContacts)
  const emailIntegration = useAppSelector(selectCurrentUserEmailIntegration)

  const [triggerFilterEmailThreads] = useFilterEmailThreadsMutation()
  const [triggerQueryEmailMetadata] = useLazyQueryEmailMetadataQuery()

  const fetchEmailThreads = useCallback(
    async ({
      filterId,
      filter,
      clear = true,
    }: {
      filterId?: string | null
      filter?: EmailFilterPayload | null
      clear?: boolean
    } = {}) => {
      if (!emailIntegration || !contacts.length) {
        return
      }
      const { serviceId: emailConnector } = emailIntegration
      if (clear) {
        dispatch(clearEmailThreads())
      }
      dispatch(setEmailThreadsLoading(true))
      dispatch(setEmailThreadsHasMore(true))
      try {
        const { data: emailThreads } = await triggerFilterEmailThreads({
          accountId,
          emailConnector,
          filters: { ...(filterId && { filterId }), ...(filter && { filter }) },
        })
        if (!emailThreads?.data) {
          return
        }
        dispatch(setEmailThreads(emailThreads.data))
        dispatch(setEmailThreadsInitialized())
        dispatch(setEmailThreadsHasMore(!!emailThreads.data.length))
        const contactEmailIds = emailThreads.data
          .flatMap((thread) => thread.emails)
          .filter((email) => contacts.some((contact) => contact.email?.toLowerCase() === email.sender.email.toLowerCase()))
          .map((email) => email.externalId)
        if (contactEmailIds.length) {
          dispatch(setEmailMetadataLoading(true))
          const { data: emailMetadata } = await triggerQueryEmailMetadata({
            accountId,
            emailConnector,
            externalIds: contactEmailIds,
          })
          if (emailMetadata?.data) {
            dispatch(setEmailMetadata(emailMetadata.data))
          }
        }
      } finally {
        dispatch(setEmailThreadsLoading(false))
        dispatch(setEmailMetadataLoading(false))
      }
    },
    [dispatch, accountId, contacts, emailIntegration, triggerFilterEmailThreads, triggerQueryEmailMetadata],
  )

  return { fetchEmailThreads }
}

export const useFetchEmailThread = () => {
  const accountId = useAppSelector(selectAccountId)

  const dispatch = useAppDispatch()

  const emailIntegration = useAppSelector(selectCurrentUserEmailIntegration)
  const contacts = useAppSelector(selectEmailContacts)

  const [triggerGetEmailThread] = useLazyGetEmailThreadQuery()
  const [triggerQueryEmailMetadata] = useLazyQueryEmailMetadataQuery()

  const fetchEmailThread = useCallback(
    async (threadId: string) => {
      if (!emailIntegration || !contacts.length) {
        return
      }
      const { serviceId: emailConnector } = emailIntegration
      dispatch(setEmailThreadsLoading(true))
      try {
        const { data: emailThread } = await triggerGetEmailThread({
          accountId,
          emailConnector,
          threadId,
        })

        if (!emailThread) {
          return
        }

        dispatch(setEmailThread(emailThread))
        const contactEmailIds = emailThread.emails
          .filter((email) => contacts.some((contact) => contact.email?.toLowerCase() === email.sender.email.toLowerCase()))
          .map((email) => email.externalId)
        if (contactEmailIds.length) {
          dispatch(setEmailMetadataLoading(true))
          const { data: emailMetadata } = await triggerQueryEmailMetadata({
            accountId,
            emailConnector,
            externalIds: contactEmailIds,
          })
          if (emailMetadata?.data) {
            dispatch(setEmailMetadata(emailMetadata.data))
          }
        }
      } finally {
        dispatch(setEmailThreadsLoading(false))
        dispatch(setEmailMetadataLoading(false))
      }
    },
    [dispatch, accountId, contacts, emailIntegration, triggerGetEmailThread, triggerQueryEmailMetadata],
  )

  return { fetchEmailThread }
}

export const useFetchEmailResponseTemplate = () => {
  const accountId = useAppSelector(selectAccountId)
  const currentUserId = useAppSelector(selectSafeAuthenticatedUser).id
  const emailTypes = useAppSelector(selectEmailTypes)

  const [getInputParameterHierarchyNode] = useLazyGetEffectiveNodeQuery()
  const [getInputParameterHierarchyDefault] = useLazyGetInputParametersHierarchyDefaultQuery()

  const fetchEmailResponseTemplate = useCallback(
    async (metadata: EmailMetadata | null): Promise<{ template: string; emailType: EmailType | null }> => {
      const emailTypeId = metadata?.typeProcessing?.emailTypeId
      const emailType = emailTypeId ? (emailTypes.find((type) => type.id === emailTypeId) ?? null) : null
      if (!emailType) {
        const defaultTemplate = await getInputParameterHierarchyDefault(
          {
            accountId,
            type: InputParameterHierarchyType.EMAIL_RESPONSE_TEMPLATE,
            typeId: 'default-email',
          },
          true,
        ).unwrap()
        return { template: defaultTemplate?.inputParameters['template'] ?? '', emailType }
      }

      const template = await getInputParameterHierarchyNode(
        {
          accountId,
          type: InputParameterHierarchyType.EMAIL_RESPONSE_TEMPLATE,
          typeId: 'email-template',
          context: { effective: true, userId: currentUserId, emailTypeId: emailType.id, contextType: 'email_type' },
        },
        true,
      ).unwrap()
      if (template) {
        return { template: template.inputParameters['template'] ?? '', emailType }
      }
      const defaultTemplate = await getInputParameterHierarchyDefault(
        {
          accountId,
          type: InputParameterHierarchyType.EMAIL_RESPONSE_TEMPLATE,
          typeId: emailType.systemEmailTypeId ?? 'default-email',
        },
        true,
      ).unwrap()
      if (defaultTemplate) {
        return { template: defaultTemplate.inputParameters['template'] ?? '', emailType }
      }
      return { template: '', emailType }
    },
    [accountId, currentUserId, emailTypes, getInputParameterHierarchyDefault, getInputParameterHierarchyNode],
  )

  return { fetchEmailResponseTemplate }
}

export const useFetchEmailThreadTasks = () => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)

  const [triggerGetTasks] = useLazyGetAllTasksQuery()

  const fetchTasks = useCallback(
    async (emailConnector: string, threadId: string) => {
      dispatch(setTasksLoading(true))
      try {
        const filters = { type: 'email', emailConnector, threadId }
        const { data: tasks } = await triggerGetTasks({ accountId, filters }).unwrap()
        dispatch(setTasks(tasks))
      } catch (e) {
        console.log(e)
      } finally {
        dispatch(setTasksLoading(false))
      }
    },
    [accountId, dispatch, triggerGetTasks],
  )

  return { fetchTasks }
}

export const useFetchThreadAttachments = () => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)

  const [triggerGetEmailThreadAttachments] = useLazyGetEmailThreadAttachmentsQuery()

  const fetchThreadAttachments = useCallback(
    async (emailConnector: string, threadId: string) => {
      dispatch(setEmailThreadAttachmentsLoading(true))
      try {
        const attachments = await triggerGetEmailThreadAttachments({
          accountId: accountId,
          connector: emailConnector,
          threadId: threadId,
        }).unwrap()
        dispatch(setEmailThreadAttachments(attachments))
      } catch (e) {
        console.log(e)
      } finally {
        dispatch(setEmailThreadAttachmentsLoading(false))
      }
    },
    [accountId, dispatch, triggerGetEmailThreadAttachments],
  )

  return { fetchThreadAttachments }
}
